import { useEffect, useState, useRef, useCallback } from 'react';
import { useOnboardingTour } from '@/hooks/use-onboarding-tour';

interface TooltipPosition {
  top: number;
  left: number;
}

function getTooltipPosition(
  rect: DOMRect,
  placement: 'top' | 'bottom' | 'left' | 'right' = 'bottom',
): TooltipPosition {
  const OFFSET = 12;
  switch (placement) {
    case 'top':
      return { top: rect.top - OFFSET, left: rect.left + rect.width / 2 };
    case 'bottom':
      return { top: rect.bottom + OFFSET, left: rect.left + rect.width / 2 };
    case 'left':
      return { top: rect.top + rect.height / 2, left: rect.left - OFFSET };
    case 'right':
      return { top: rect.top + rect.height / 2, left: rect.right + OFFSET };
  }
}

export function OnboardingTour() {
  const tour = useOnboardingTour();
  const [tooltipPos, setTooltipPos] = useState<TooltipPosition | null>(null);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (!tour.isActive || !tour.step) return;
    const el = document.querySelector(tour.step.target);
    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect(rect);
      setTooltipPos(getTooltipPosition(rect, tour.step.placement));
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      setTargetRect(null);
      setTooltipPos(null);
    }
  }, [tour.isActive, tour.step]);

  useEffect(() => {
    const rafId = requestAnimationFrame(updatePosition);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [updatePosition]);

  if (!tour.isActive || !tooltipPos || !targetRect || !tour.step) {
    return null;
  }

  const placement = tour.step.placement ?? 'bottom';

  return (
    <div className="fixed inset-0 z-[9999]" role="dialog" aria-label="Onboarding tour">
      {/* Backdrop with cutout */}
      <svg className="absolute inset-0 w-full h-full" aria-hidden="true">
        <defs>
          <mask id="tour-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={targetRect.left - 4}
              y={targetRect.top - 4}
              width={targetRect.width + 8}
              height={targetRect.height + 8}
              rx="8"
              fill="black"
            />
          </mask>
        </defs>
        <rect
          x="0" y="0" width="100%" height="100%"
          fill="rgba(0,0,0,0.5)"
          mask="url(#tour-mask)"
        />
      </svg>

      {/* Spotlight ring */}
      <div
        className="absolute border-2 border-teal-400 rounded-lg pointer-events-none"
        style={{
          top: targetRect.top - 4,
          left: targetRect.left - 4,
          width: targetRect.width + 8,
          height: targetRect.height + 8,
        }}
      />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        role="tooltip"
        className="absolute bg-white rounded-xl shadow-2xl p-5 w-80 z-[10000]"
        style={{
          top: placement === 'top' ? tooltipPos.top - 8 : placement === 'bottom' ? tooltipPos.top : tooltipPos.top,
          left: placement === 'left' ? tooltipPos.left - 328 : placement === 'right' ? tooltipPos.left : tooltipPos.left - 160,
          transform: placement === 'top' ? 'translateY(-100%)' : undefined,
        }}
      >
        {/* Step indicator */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-medium text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">
            {tour.currentStep + 1} of {tour.totalSteps}
          </span>
          <button
            onClick={tour.skip}
            className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Skip tour"
          >
            Skip tour
          </button>
        </div>

        <h3 className="text-base font-semibold text-gray-900 mb-1.5">{tour.step.title}</h3>
        <p className="text-sm text-gray-600 leading-relaxed mb-4">{tour.step.content}</p>

        {/* Progress dots */}
        <div className="flex items-center justify-between">
          <div className="flex gap-1.5">
            {Array.from({ length: tour.totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full transition-colors ${
                  i === tour.currentStep ? 'bg-teal-500' : i < tour.currentStep ? 'bg-teal-300' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>

          <div className="flex gap-2">
            {!tour.isFirstStep && (
              <button
                onClick={tour.prev}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Back
              </button>
            )}
            <button
              onClick={tour.next}
              className="px-4 py-1.5 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors"
            >
              {tour.isLastStep ? 'Get Started' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
